import {
  Link,
  Text,
  Wrap,
} from '@chakra-ui/react';
import {
  useRoadsNetworkLength, useRoadsUrbanLength,
  useRoadsUrbanRuralLength,
} from '@src/modules/performance/context/reports-api-hooks';
import Chart from 'react-apexcharts';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import StackedBarChart from '@src/modules/performance/components/charts/StackedBarChart';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Roads',
  content: [
    {
      title: 'Road network (Sealed/Unsealed)',
      description:
  <Text fontSize="inherit" mb={2}>
    The split of the road network by sealed and unsealed roads, for total lane km and total centreline km.
  </Text>,
      source: null,
    },
    {
      title: 'Road network (Urban/Rural)',
      description:
  <Text fontSize="inherit" mb={2}>
    The split of the road network by urban and rural roads, for total lane km and total centreline km.
  </Text>,
      source: null,
    },
    {
      title: 'Urban percentage by length',
      description:
  <Text fontSize="inherit" mb={2}>
    The percentage of the road network length, which is defined as urban (having a speed limit of less than 70km/hr).
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>NZTA, physical statistics - roads</Link>
  </Text>,
    },
  ],
};

function NetworkPhysicalCharacteristicsRoads() {
  const { data: roadsNetworkLength, isLoading: isLoadingNetworkLength } = useRoadsNetworkLength();
  const { data: roadsUrbanRuralLength, isLoading: isLoadingUrbanRuralLength } = useRoadsUrbanRuralLength();
  const { data: roadsUrbanLength, isLoading: isLoadingUrbanLength } = useRoadsUrbanLength();
  const isLoading = isLoadingNetworkLength || isLoadingUrbanLength || isLoadingUrbanRuralLength;

  // Network length data
  const networkLengthYears = [...new Set(roadsNetworkLength?.map((x) => x.Year))];
  const networkLengthSeries = [
    {
      name: 'Sealed km',
      group: 'Length',
      data: roadsNetworkLength
        ?.filter((x) => x.Type?.toLowerCase() === 'sealed')
        .map((item) => ({
          x: item.Year,
          y: item.Length,
        })),
      color: CHART_COLOURS.purple,
      hidden: true,
    },
    {
      name: 'Unsealed km',
      group: 'Length',
      data: roadsNetworkLength
        ?.filter((x) => x.Type?.toLowerCase() === 'unsealed')
        .map((item) => ({
          x: item.Year,
          y: item.Length,
        })),
      color: CHART_COLOURS.yellow,
      hidden: true,
    },
    {
      name: 'Sealed lane km',
      group: 'LaneKm',
      data: roadsNetworkLength
        ?.filter((x) => x.Type?.toLowerCase() === 'sealed')
        .map((item) => ({
          x: item.Year,
          y: item.LaneKm,
        })),
      color: CHART_COLOURS.blue,
    },
    {
      name: 'Unsealed lane km',
      group: 'LaneKm',
      data: roadsNetworkLength
        ?.filter((x) => x.Type?.toLowerCase() === 'unsealed')
        .map((item) => ({
          x: item.Year,
          y: item.LaneKm,
        })),
      color: CHART_COLOURS.red,
    },
  ];

  // Roads urban/rural length data
  const urbanRuralLengthYears = [...new Set(roadsUrbanRuralLength?.map((x) => x.Year))];
  const urbanRuralLengthSeries = [
    {
      name: 'Urban km',
      group: 'Length',
      data: roadsUrbanRuralLength
        ?.map((item) => ({
          x: item.Year,
          y: item.UrbanKm,
        })),
      color: CHART_COLOURS.purple,
      hidden: true,
    },
    {
      name: 'Rural km',
      group: 'Length',
      data: roadsUrbanRuralLength
        ?.map((item) => ({
          x: item.Year,
          y: item.RuralKm,
        })),
      color: CHART_COLOURS.yellow,
      hidden: true,
    },
    {
      name: 'Urban lane km',
      group: 'LaneKm',
      data: roadsUrbanRuralLength
        ?.map((item) => ({
          x: item.Year,
          y: item.UrbanLaneKm,
        })),
      color: CHART_COLOURS.blue,
    },
    {
      name: 'Rural lane km',
      group: 'LaneKm',
      data: roadsUrbanRuralLength
        ?.map((item) => ({
          x: item.Year,
          y: item.RuralLaneKm,
        })),
      color: CHART_COLOURS.red,
    },
  ];

  // Urban length data
  const urbanLengthYears = [...new Set(roadsUrbanLength?.map((x) => x.Year))];
  const urbanLengthSeries = [
    {
      name: 'Urban',
      type: 'line',
      data: roadsUrbanLength?.map((item) => ({
        x: item.Year,
        y: item.Urban_pc * 100,
      })),
      color: CHART_COLOURS.blue,
    },
  ];

  return (
    <ReportSection
      title="Roads"
      isLoading={isLoading || !roadsNetworkLength || !roadsUrbanLength}
      informationSource={informationSource}
      lockYear={roadsNetworkLength?.[0]?.LockYear || null}
    >
      <Wrap>
        <StackedBarChart
          title="Road network (Sealed/Unsealed)"
          categories={networkLengthYears}
          series={networkLengthSeries}
          width="100%"
          height="280"
          prefix=""
          suffix="km"
        />
        <StackedBarChart
          title="Road network (Urban/Rural)"
          categories={urbanRuralLengthYears}
          series={urbanRuralLengthSeries}
          width="100%"
          height="280"
          prefix=""
          suffix="km"
        />
        <Chart
          options={
            {
              title: {
                text: 'Urban % by length',
              },
              chart: {
                animations: {
                  enabled: false,
                },
              },
              xaxis: {
                categories: urbanLengthYears,
                labels: {
                  offsetY: 5,
                  rotate: -45,
                  rotateAlways: true,
                },
              },
              yaxis: [
                {
                  forceNiceScale: false,
                  labels: {
                    formatter(y) {
                      return `${y.toFixed(1)}%`;
                    },
                  },
                },
              ],
              grid: {
                padding: {
                  left: 20,
                },
              },
              dataLabels: {
                enabled: false,
              },
              tooltip: {
                shared: true,
                intersect: false,
                y: {
                  formatter: (val) => `${val.toFixed(1)}%`,
                },
              },
            }
          }
          series={urbanLengthSeries}
          type="line"
          width="100%"
          height="280"
        />
      </Wrap>
    </ReportSection>
  );
}

export default NetworkPhysicalCharacteristicsRoads;
